<template>
  <div class="scrubber">
    <div class="scrubber__columns">
      <p
        v-if="showTime && !isLiveStream"
        class="scrubber__time scrubber__time--elapsed"
        :style="themeStyles"
      >
        {{ formatTime(timeElapsed) }}
      </p>
      <input
        v-if="!isLiveStream"
        type="range"
        class="scrubber__input"
        :class="`scrubber__input--${theme.type}`"
        :style="progressFillStyles"
        :value="position"
        :disabled="isLoading"
        @input="update"
      />
      <p
        v-if="showTime && !isLiveStream"
         class="scrubber__time scrubber__time--remaining"
         :style="themeStyles"
        >
          {{ formatTime(timeRemaining) }}
        </p>
    </div>
    <div v-if="isLiveStream" class="scrubber__live-stream" :style="`background: ${fill};`"></div>
  </div>
</template>

<script>
export default {
  name: 'scrubber',
  // Vue3 (LP-4/MR-78 emits parity — mirrors the v1/v4 Scrubber siblings): declare
  // `seeking` so the parent Player's `@seeking` binds as a component event.
  emits: ['seeking'],
  props: {
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    showTime: {
      type: Boolean,
      default() {
        return true;
      },
    },
    isLoading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    duration: {
      type: Number,
      default() {
        return 0;
      },
    },
    position: {
      type: Number,
      default() {
        return 0;
      },
    },
    timeElapsed: {
      type: Number,
      default() {
        return 0;
      },
    },
    timeRemaining: {
      type: Number,
      default() {
        return 0;
      },
    },
    isLiveStream: {
      type: Boolean,
      default() {
        return false;
      },
    },
    podcastId: Number,
  },
  computed: {
    barColor() {
      return ['dark', 'custom'].includes(this.theme.type.toLowerCase().trim()) ? '#fff' : '#dadae0';
    },

    progressFillStyles() {
      const position = this.position;
      const barColor = this.barColor;
      return {
        background: `linear-gradient(
          to right,
          ${this.fill} 0%,
          ${this.fill} ${position}%,
          ${barColor} ${position}%,
          ${barColor} 100%
        )`,
      };
    },

    fill() {
      let fill;

      switch (this.theme.type) {
        case 'light':
          fill = '#532AA2';
          break;

        default:
          fill = '#DADAE0';
      }

      return fill;
    },

    themeStyles() {
      return {
        color: this.theme.text,
      };
    },
  },
  methods: {
    update(event) {
      // The element is back at readyState 0 with duration NaN until `canplay`, and
      // Player.onSeeking multiplies by that duration. `disabled` above stops a real
      // drag; this stops a synthetic `input` event, which fires the handler
      // regardless of the attribute — so the contract lives here (D-98).
      if (this.isLoading) return;
      this.$emit('seeking', +event.target.value);
    },

    /**
     * Formats seconds into hh:mm:ss. Was a Vue1 `filters:` entry
     * (`{{ x | formatTime }}`); Vue3 dropped filters, so this is a method the
     * template calls directly (`{{ formatTime(x) }}`).
     *
     * @params {number} seconds The number of seconds to format
     * @return {string}
     */
    formatTime(seconds) {
      let sec = seconds; // prevent param reassignment

      const hours = Math.floor(sec / 3600.0);
      sec -= hours * 3600.0;

      const minutes = Math.floor(sec / 60.0);
      sec -= minutes * 60.0;

      const zeroPad = (value) => {
        const str = Math.floor(value).toString();

        if (str.length < 2) {
          return `0${str}`;
        }

        return str;
      };

      const time = `${zeroPad(minutes)}:${zeroPad(sec)}`;

      if (hours > 0) {
        return `${Math.floor(hours).toString()}:${time}`;
      }

      return time;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/variables-v2';

.scrubber {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  overflow: hidden;
  &__columns {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 -0.35rem;
  }
  &__input {
    // D-98 review fold: `disabled` inside the load window, with the handler
    // returning early — so a drag did nothing and said nothing. Give the state an
    // affordance. Only matches inside the window, which no visual cell captures.
    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
    flex: 1 0 auto;
    -webkit-appearance: none;
    appearance: none;
    padding: 0 0.35rem;
    height: 0.25rem;
    border-radius: 4px;
    margin: 0;
    padding: 0;
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      height: 0.5rem;
      width: 0.5rem;
      border-radius: 50%;
    }
    &--light {
      &::-webkit-slider-thumb {
        background: #532AA2;
      }
    }
    &--dark,
    &--custom {
      &::-webkit-slider-thumb {
        background: #DADAE0;
      }
    }
  }

  &__time {
    flex: 0 0 auto;
    padding: 0 0.35rem;
    color: #6e6e7a;
    font-family: $font;
    font-size: 10px;
  }

  &__live-stream {
    padding: 0 0.35rem;
    height: 0.25rem;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      background: -moz-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(128,186,232,0) 99%, rgba(125,185,232,0) 100%);
      background: -webkit-gradient(linear, left top, right top, color-stop(0%,rgba(255, 255, 255, 0)), color-stop(50%,rgba(255, 255, 255, 0.8)), color-stop(99%,rgba(128,186,232,0)), color-stop(100%,rgba(125,185,232,0)));
      background: -webkit-linear-gradient(left, rgba(255, 255, 255, 0) 0%,rgba(255, 255, 255, 0.8) 50%,rgba(128,186,232,0) 99%,rgba(125,185,232,0) 100%);
      background: -o-linear-gradient(left, rgba(255, 255, 255, 0) 0%,rgba(255, 255, 255, 0.8) 50%,rgba(128,186,232,0) 99%,rgba(125,185,232,0) 100%);
      background: -ms-linear-gradient(left, rgba(255, 255, 255, 0) 0%,rgba(255, 255, 255, 0.8) 50%,rgba(128,186,232,0) 99%,rgba(125,185,232,0) 100%);
      background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%,rgba(255, 255, 255, 0.8) 50%,rgba(128,186,232,0) 99%,rgba(125,185,232,0) 100%);
      filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#00ffffff', endColorstr='#007db9e8', GradientType=1);
	    animation: shine 2s infinite;
    }
  }
}

@keyframes shine {
	0% {
    transform: translateX(-100%);
  }
	100% {
    transform: translateX(100%);
  }
}
</style>
